/**
 * MapViewQ3Timeline — cuadrante 3 del MapView en modo split.
 *
 * Timeline del día del sitio (decisión Paulo 2026-06-11). Agrupa los
 * eventos del sitio por hora y por tipo, muestra una mini-barra por bucket.
 * Click en un bucket dispara onJumpTo(timestamp) para que el padre abra
 * la galería forense o playback en ese momento.
 *
 * Datos: /api/edge/events?site_id={id}&from={today_start}&limit=1000
 *        (endpoint que ya consumimos en ForensicGallery)
 *
 * Visualización: ribbon horizontal con 24 columnas (una por hora).
 * Cada columna tiene un stack vertical de chips por tipo, escalado por
 * volumen. Hora actual marcada con barra vertical.
 *
 * Refresca cada 30s para captar eventos nuevos del día.
 */

import { useState, useEffect, useMemo } from 'react'


const EVENT_COLORS = {
  weapon_detected   : '#d93025',
  loitering_detected: '#f59e0b',
  loitering         : '#f59e0b',
  line_crossing     : '#dc2626',
  face_match        : '#8b5cf6',
  vehicle_detected  : '#0091ff',
  person_detected   : '#10b981',
  person_alert      : '#10b981',
  intrusion         : '#dc2626',
  lpr               : '#06b6d4',
  incident          : '#d93025',
}

const EVENT_SHORT = {
  weapon_detected   : 'Arma',
  loitering_detected: 'Mero',
  loitering         : 'Mero',
  line_crossing     : 'Cruce',
  face_match        : 'Cara',
  vehicle_detected  : 'Veh',
  person_detected   : 'Per',
  person_alert      : 'PerA',
  intrusion         : 'Intr',
  lpr               : 'LPR',
  incident          : 'Inc',
}


export default function MapViewQ3Timeline({
  site,
  token,
  onJumpTo,         // (timestamp_iso) => void al clickear un bucket
}) {
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => {
    if (!site?.id || !token) return
    let cancelled = false
    let intervalId = null

    const fetchEvents = async () => {
      try {
        const fromIso = new Date()
        fromIso.setHours(0, 0, 0, 0)
        const r = await fetch(
          `/api/edge/events?site_id=${site.id}&from=${fromIso.toISOString()}&limit=1000`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const data = await r.json()
        if (!cancelled) {
          setEvents(Array.isArray(data) ? data : (data.items || []))
          setErr(null)
        }
      } catch (e) {
        if (!cancelled) setErr(e.message)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    setLoading(true)
    fetchEvents()
    intervalId = setInterval(fetchEvents, 30000)
    return () => {
      cancelled = true
      if (intervalId) clearInterval(intervalId)
    }
  }, [site?.id, token])

  // Agrupar por hora del día (0..23) y por tipo
  const buckets = useMemo(() => {
    const result = Array.from({ length: 24 }, () => ({}))
    for (const e of events) {
      // Guard contra eventos sin timestamp válido — si .timestamp y .created_at
      // son ambos undefined o mal formados, new Date() es Invalid, getHours
      // devuelve NaN y result[NaN] sería undefined → crash al asignar
      // `undefined[type] = ...`. Saltamos esos eventos.
      const rawTs = e?.timestamp || e?.created_at
      if (!rawTs) continue
      const ts = new Date(rawTs)
      const hour = ts.getHours()
      if (!Number.isFinite(hour) || hour < 0 || hour > 23) continue
      const type = e.event_type || e.type || 'incident'
      const bucket = result[hour]
      bucket[type] = (bucket[type] || 0) + 1
    }
    return result
  }, [events])

  // Volumen máximo de cualquier celda — para escalar las barras
  const maxVolume = useMemo(() => {
    let max = 0
    for (const bucket of buckets) {
      const total = Object.values(bucket).reduce((s, n) => s + n, 0)
      if (total > max) max = total
    }
    return max
  }, [buckets])

  const totalEvents = useMemo(
    () => buckets.reduce((s, b) => s + Object.values(b).reduce((a, n) => a + n, 0), 0),
    [buckets],
  )

  const currentHour = new Date().getHours()

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      width: '100%', height: '100%',
      background: 'var(--bg-surface)',
      border: '1px solid var(--border)',
      borderRadius: 8, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '8px 12px',
        background: 'var(--bg-elev-1)',
        borderBottom: '1px solid var(--border)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
          Hoy · timeline
        </div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
          {loading ? 'cargando…' : `${totalEvents} eventos`}
        </div>
      </div>

      {/* Ribbon de 24 horas */}
      <div style={{
        display: 'flex', flex: 1,
        padding: 12, gap: 2,
        alignItems: 'flex-end',
        minHeight: 120,
      }}>
        {buckets.map((bucket, hour) => {
          const total = Object.values(bucket).reduce((s, n) => s + n, 0)
          const heightPct = maxVolume > 0 ? (total / maxVolume) * 100 : 0
          const isCurrentHour = hour === currentHour
          const types = Object.keys(bucket).sort(
            (a, b) => bucket[b] - bucket[a],
          )
          return (
            <button
              key={hour}
              onClick={() => {
                if (total === 0 || !onJumpTo) return
                const ts = new Date()
                ts.setHours(hour, 0, 0, 0)
                onJumpTo(ts.toISOString())
              }}
              disabled={total === 0}
              title={total > 0
                ? `${formatHour(hour)}: ${total} eventos\n${
                    types.map(t => `  ${EVENT_SHORT[t] || t}: ${bucket[t]}`).join('\n')
                  }`
                : `${formatHour(hour)}: sin eventos`}
              style={{
                flex: 1,
                position: 'relative',
                height: '100%',
                display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
                background: 'transparent',
                border: 'none',
                borderBottom: isCurrentHour ? '2px solid #0091ff' : '1px solid var(--border)',
                cursor: total > 0 ? 'pointer' : 'default',
                padding: 0,
                minWidth: 0,
              }}
            >
              {/* Stack vertical de bandas por tipo */}
              <div style={{
                width: '100%',
                height: `${heightPct}%`,
                minHeight: total > 0 ? 4 : 0,
                display: 'flex', flexDirection: 'column-reverse',
                borderRadius: '2px 2px 0 0',
                overflow: 'hidden',
              }}>
                {types.map(t => {
                  const seg = bucket[t] / total * 100
                  return (
                    <div
                      key={t}
                      style={{
                        width: '100%',
                        height: `${seg}%`,
                        background: EVENT_COLORS[t] || '#6b7280',
                      }}
                    />
                  )
                })}
              </div>
              {/* Hora abajo */}
              <div style={{
                position: 'absolute', bottom: -16, left: 0, right: 0,
                fontSize: 8, color: 'var(--text-muted)',
                textAlign: 'center',
              }}>
                {hour % 6 === 0 ? `${hour}h` : ''}
              </div>
            </button>
          )
        })}
      </div>

      {/* Leyenda mini de tipos vistos */}
      <div style={{
        padding: '24px 12px 8px',
        borderTop: '1px solid var(--border)',
        display: 'flex', gap: 8, flexWrap: 'wrap',
        fontSize: 10, color: 'var(--text-muted)',
      }}>
        {Array.from(new Set(events.map(e => e.event_type || e.type))).slice(0, 8).map(t => (
          <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <span style={{
              width: 8, height: 8, borderRadius: 2,
              background: EVENT_COLORS[t] || '#6b7280',
            }} />
            {EVENT_SHORT[t] || t}
          </span>
        ))}
      </div>

      {err && (
        <div style={{
          padding: '4px 12px',
          background: 'rgba(217, 48, 37, 0.1)',
          color: '#d93025', fontSize: 11,
        }}>{err}</div>
      )}
    </div>
  )
}


function formatHour(h) {
  return `${String(h).padStart(2, '0')}:00`
}
